<template>
    <BaseCard padding="lg" class="flex flex-col gap-6">
        <div class="flex flex-wrap items-center justify-center gap-2 rounded-lgr bg-surface px-4 py-10">
            <BaseTooltip v-for="member in report.members" :key="member.id" :trigger-aria-label="memberLabel(member)" :as-child="false" open-on-click content-class="fs-mask">
                <template #trigger>
                    <PerspectiveAvatar :name="member.name" :letters="member.result?.letters" aria-hidden="true" />
                </template>
                <template #content>{{ memberLabel(member) }}</template>
            </BaseTooltip>
        </div>
        <div v-if="!report.is_available" class="team-overview-row">
            <h3 class="body-small-semibold text-ink">{{ t("teamPage.status") }}</h3>
            <TeamPerspectivePending :report="report" />
        </div>
        <div v-else class="team-overview-row">
            <h3 class="body-small-semibold text-ink">{{ t("teamPage.makeup") }}</h3>
            <div class="flex flex-col gap-2">
                <p class="body-small text-ink">{{ t("teamPage.makeupIntro", { count: report.result_count, total: report.members.length }) }}</p>
                <ul v-if="report.dimensions.length" class="body-small flex list-none flex-col gap-2 text-ink">
                    <li v-for="dimension in dimensions" :key="dimension.id" class="flex items-center gap-2">
                        <img v-if="dimensionIcons[dimension.id]" :src="dimensionIcons[dimension.id]" alt="" class="size-5 shrink-0" />
                        {{
                            t("teamPage.dimensionSummary", {
                                dimension: dimension.title,
                                summary:
                                    dimension.spread === "balanced"
                                        ? t("teamPage.balanced")
                                        : t("teamPage.leans", { side: dimension.average < 0 ? dimension.left_label.toLowerCase() : dimension.right_label.toLowerCase() }),
                            })
                        }}
                    </li>
                </ul>
                <p v-else class="body-small text-ink-muted">{{ t("teamPerspective.empty") }}</p>
            </div>
        </div>
        <div v-if="missingMembers.length" class="team-overview-row">
            <h3 class="body-small-semibold text-ink">{{ t("teamPage.missingTitle") }}</h3>
            <ul class="body-small flex list-none flex-col gap-2 text-ink-muted">
                <li v-for="member in missingMembers" :key="member.id">
                    <span class="text-ink">{{ member.name }}</span> {{ t("teamPage.missingStatus") }}
                </li>
            </ul>
        </div>
        <div v-if="report.is_available" class="team-overview-row border-t border-border pt-6">
            <h3 class="body-small-semibold text-ink">{{ t("teamPage.guidance") }}</h3>
            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-10">
                    <div v-for="dimension in dimensions" :key="dimension.id" class="flex flex-col gap-2">
                        <h4 class="body-small-semibold flex items-center gap-2 text-ink">
                            <img v-if="dimensionIcons[dimension.id]" :src="dimensionIcons[dimension.id]" alt="" class="size-5 shrink-0" />{{ dimension.title }}
                        </h4>
                        <MarkdownText :text="dimension.guidance.overview" class="body-small text-ink-muted" />
                    </div>
                </div>
            </div>
        </div>
    </BaseCard>
</template>
<script setup lang="ts">
import decisionMakingIcon from "~img/perspective/decision-making.svg";
import energyIcon from "~img/perspective/energy.svg";
import infoProcessingIcon from "~img/perspective/information-processing.svg";
import organizationIcon from "~img/perspective/organization.svg";
import type { TeamPerspectiveMember, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import TeamPerspectivePending from "~vue/Perspective/TeamPerspectivePending.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { report } = defineProps<{ report: TeamPerspectiveReport }>();
const { t } = useI18n();
const dimensionIcons: Readonly<Record<string, string>> = { EI: energyIcon, SN: infoProcessingIcon, TF: decisionMakingIcon, JP: organizationIcon };
const dimensions = computed(() => [...report.dimensions].sort((a, b) => ["EI", "SN", "TF", "JP"].indexOf(a.id) - ["EI", "SN", "TF", "JP"].indexOf(b.id)));
const missingMembers = computed(() => report.members.filter((member) => !member.result?.dimensions.length));
function memberLabel(member: TeamPerspectiveMember) {
    const name = member.id === report.viewer_id ? t("teamPage.youName", { name: member.name }) : member.name;
    return t("perspective.team.memberWithDetail", { name, detail: member.result?.archetype || t("teamPage.noSharedResult") });
}
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .team-overview-row {
        @apply grid gap-3 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-6;
    }
}
</style>
